import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

const api = axios.create({ baseURL: API_BASE_URL });

const getToken = () => localStorage.getItem('token');

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getToken()}`
});

// Helper to call the non-streaming backend proxy
const aiComplete = async (messages, { model = 'gpt-4o-mini', max_tokens = 2000, temperature = 0.7, response_format } = {}) => {
  const body = { messages, model, max_tokens, temperature };
  if (response_format) body.response_format = response_format;
  const res = await fetch(`${API_BASE_URL}/api/ai/complete`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || `AI request failed: ${res.status}`);
  }
  const data = await res.json();
  return data.choices[0].message.content;
};

const isTechnicalQuestion = (question) => {
  const technicalKeywords = [
    'code', 'program', 'algorithm', 'function', 'class', 'method',
    'write a', 'how would you', 'implement', 'develop', 'create a',
    'debug', 'fix', 'optimize', 'database', 'sql', 'api', 'endpoint',
    'html', 'css', 'javascript', 'python', 'java', 'c#', 'c++', 'react',
    'node', 'framework', 'library', 'git', 'devops', 'server', 'client',
    'frontend', 'backend', 'fullstack', 'variable', 'loop', 'array',
    'object', 'string', 'integer', 'boolean', 'max', 'min', 'sort'
  ];
  return technicalKeywords.some(k => question.toLowerCase().includes(k));
};

const isBehavioralQuestion = (question) => {
  const behavioralKeywords = [
    'experience', 'worked', 'team', 'collaborat', 'challenge', 'problem',
    'solve', 'conflict', 'manage', 'lead', 'example', 'situation',
    'tell me about', 'describe a time', 'how did you', 'what would you',
    'behavior', 'approach', 'handle', 'respond', 'react', 'motivat',
    'strength', 'weakness', 'goal', 'ambition', 'career', 'professional',
    'accomplish', 'achieve', 'proud', 'mistake', 'learn', 'grow'
  ];
  return behavioralKeywords.some(k => question.toLowerCase().includes(k));
};

export const getAIResponse = async (interviewerQuestion, jobData, conversationHistory = [], onChunk) => {
  try {
    const recentHistory = conversationHistory.slice(-6);
    const isTechnical = isTechnicalQuestion(interviewerQuestion);
    const isBehavioral = isBehavioralQuestion(interviewerQuestion);

    let systemPrompt = '';
    if (isTechnical) {
      systemPrompt = `You are an elite interview coach generating a spoken answer for a real job interview. The candidate is applying for: ${jobData.jobRole}.

CANDIDATE RESUME:
${jobData.resumeText}

JOB DESCRIPTION:
${jobData.jobDescription}

YOUR GOAL: Write the exact words the candidate should say out loud in the interview. The answer must sound like a highly experienced, confident senior professional — not a textbook definition.

RULES:
1. Write ONLY in first person ("I", "my", "we" when referring to team). Never say "the candidate" or "you".
2. Open by directly and confidently answering the question — no filler phrases like "Great question!" or "Sure!".
3. Explain the concept clearly in plain English, the way an expert explains it to a peer, not a student.
4. Connect the answer to a SPECIFIC real experience from the resume — name the actual company, project, or technology used. Make it personal and credible.
5. Mention a real challenge or trade-off encountered and how it was handled — this shows depth.
6. Use precise technical language appropriate for a senior-level interview.
7. End with a confident insight, lesson learned, or forward-looking statement that shows maturity.
8. Length: 8 to 10 natural sentences. No bullet points. No headers. Flowing spoken prose only.
9. Tone: confident, thoughtful, and human — like someone who genuinely knows their craft.`;
    } else if (isBehavioral) {
      systemPrompt = `You are an elite interview coach generating a spoken behavioral answer for a real job interview. The candidate is applying for: ${jobData.jobRole}.

CANDIDATE RESUME:
${jobData.resumeText}

JOB DESCRIPTION:
${jobData.jobDescription}

YOUR GOAL: Write the exact words the candidate should say out loud. The answer must be a compelling, authentic story that would impress a senior hiring manager or director-level interviewer.

RULES:
1. Write ONLY in first person. Never say "the candidate" or describe them in third person.
2. Follow the STAR method but make it flow as a natural story — not a rigid template with labels.
3. Name the actual company, project, or team from the resume. Generic answers are forgettable.
4. Include one sentence of genuine personal reflection at the end.
5. Length: 8 to 12 natural sentences. No bullet points. No headers. Flowing spoken prose only.
6. Tone: warm, confident, and authentic.`;
    } else {
      systemPrompt = `You are an elite interview coach generating a spoken answer for a real job interview. The candidate is applying for: ${jobData.jobRole}.

CANDIDATE RESUME:
${jobData.resumeText}

JOB DESCRIPTION:
${jobData.jobDescription}

YOUR GOAL: Write the exact words the candidate should say out loud. The answer must sound polished, genuine, and memorable.

RULES:
1. Write ONLY in first person. Never say "the candidate" or refer to them in third person.
2. Open with a clear, direct answer to the question — no stalling, no filler phrases.
3. Back up every claim with a specific example from the resume.
4. Close with something forward-looking that shows ambition and clarity of purpose.
5. Length: 8 to 10 natural sentences. No bullet points. No headers. Flowing spoken prose only.
6. Tone: confident, genuine, and engaging.`;
    }

    const messages = [
      { role: 'system', content: systemPrompt },
      ...recentHistory.map(msg => ({
        role: msg.sender === 'ai' ? 'assistant' : 'user',
        content: msg.message
      })),
      { role: 'user', content: interviewerQuestion }
    ];

    const response = await fetch(`${API_BASE_URL}/api/ai/stream`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        messages,
        max_tokens: 1200,
        temperature: isTechnical ? 0.4 : 0.72
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`AI error: ${response.status} - ${errorData.error || 'Unknown error'}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value);
      const lines = chunk.split('\n').filter(line => line.startsWith('data: '));
      for (const line of lines) {
        const data = line.replace('data: ', '');
        if (data === '[DONE]') break;
        try {
          const parsed = JSON.parse(data);
          const token = parsed.choices[0]?.delta?.content || '';
          if (token) {
            fullText += token;
            if (onChunk) onChunk(fullText);
            await new Promise(resolve => setTimeout(resolve, 30));
          }
        } catch (e) {}
      }
    }
    return fullText;
  } catch (error) {
    console.error('Error getting AI response:', error);
    return `I'd like to answer that based on my experience in the field.`;
  }
};

export const extractKeywords = async (jobDescription) => {
  try {
    const content = await aiComplete([
      { role: 'system', content: 'You are a helpful assistant that extracts important keywords and skills from job descriptions. Return only a comma-separated list of the important keywords, without any additional text.' },
      { role: 'user', content: `Extract all important keywords and skills from this job description: ${jobDescription}` }
    ], { max_tokens: 200, temperature: 0.3 });
    return content.split(',').map(k => k.trim()).filter(k => k.length > 0);
  } catch (error) {
    console.error('Error extracting keywords:', error);
    throw error;
  }
};

export const tailorResume = async (resumeText, jobDescription, keywords) => {
  try {
    const extractKeywordsFromJobDesc = (text) => {
      const foundKeywords = new Set();
      const techTerms = [
        'JavaScript', 'Python', 'Java', 'C#', 'C++', 'React', 'Angular',
        'Vue', 'Node.js', 'SQL', 'NoSQL', 'MySQL', 'PostgreSQL', 'MongoDB',
        'AWS', 'Azure', 'GCP', 'Docker', 'Kubernetes', 'Git', 'REST API',
        'GraphQL', 'Agile', 'Scrum', 'CI/CD', 'DevOps', 'Machine Learning',
        'TypeScript', 'Express', 'Django', 'Flask', 'Spring Boot'
      ];
      const textLower = text.toLowerCase();
      techTerms.forEach(term => { if (textLower.includes(term.toLowerCase())) foundKeywords.add(term); });
      return Array.from(foundKeywords);
    };

    let aiExtractedKeywords = [];
    try {
      const content = await aiComplete([
        { role: 'system', content: 'You are a keyword extraction expert. Return only valid JSON.' },
        { role: 'user', content: `Extract the most important keywords from this job description. Return format: {"keywords": ["k1","k2"]}\n\n${jobDescription}` }
      ], { max_tokens: 500, temperature: 0, response_format: { type: 'json_object' } });
      const result = JSON.parse(content);
      aiExtractedKeywords = result.keywords || [];
    } catch (e) {}

    const patternKeywords = extractKeywordsFromJobDesc(jobDescription);
    const allJobKeywords = [...new Set([...patternKeywords, ...aiExtractedKeywords, ...keywords])];
    const resumeLower = resumeText.toLowerCase();
    const missingKeywords = allJobKeywords.filter(k => !resumeLower.includes(k.toLowerCase()));

    const tailorPrompt = `You are a professional resume writer and ATS optimization expert. Tailor this resume to match the job description by strategically incorporating missing keywords.

JOB DESCRIPTION:
${jobDescription}

CURRENT RESUME:
${resumeText}

MISSING KEYWORDS THAT MUST BE ADDED:
${missingKeywords.join(', ')}

CRITICAL INSTRUCTIONS:
1. Incorporate ALL missing keywords naturally throughout the resume
2. DO NOT fabricate experience
3. Maintain authenticity — make keyword additions sound natural
4. Use both acronyms and full terms for ATS optimization
5. Keep the same overall structure
6. Use clear section headers

Return the complete tailored resume.`;

    return await aiComplete([
      { role: 'system', content: 'You are a professional resume writer and ATS expert.' },
      { role: 'user', content: tailorPrompt }
    ], { max_tokens: 2000, temperature: 0.4 });
  } catch (error) {
    console.error('Error tailoring resume:', error);
    throw error;
  }
};

export const optimizeLinkedIn = async (linkedInUrl, targetRole, targetIndustry, jobDescription, profileData) => {
  try {
    const profileContext = profileData ? `
Name: ${profileData.fullName || ''}
Current Title: ${profileData.jobTitle || ''}
Location: ${profileData.location || ''}
Summary: ${profileData.professionalSummary || ''}
Skills: ${profileData.skills || ''}
Experience: ${JSON.stringify(profileData.workExperience || [])}
Education: ${JSON.stringify(profileData.education || [])}
Projects: ${JSON.stringify(profileData.projects || [])}
Certifications: ${profileData.certifications || ''}`.trim() : 'No profile data provided.';

    const prompt = `You are an expert LinkedIn profile optimizer and career coach.

Optimize the LinkedIn profile for:
TARGET ROLE: ${targetRole}
TARGET INDUSTRY: ${targetIndustry || 'Not specified'}
LINKEDIN URL: ${linkedInUrl || 'Not provided'}

PROFILE DATA:
${profileContext}

${jobDescription ? `Target Job Description:\n${jobDescription}` : ''}

Return ONLY a valid JSON object (no markdown):
{
  "overallScore": <number 0-100>,
  "optimizations": {
    "headline": { "issues": [], "optimized": "", "tips": [], "keywords": [] },
    "summary": { "issues": [], "optimized": "", "tips": [], "keywords": [] },
    "experience": { "issues": [], "optimized": "", "tips": [], "keywords": [] },
    "skills": { "issues": [], "optimized": "", "tips": [], "keywords": [] },
    "education": { "issues": [], "optimized": "", "tips": [], "keywords": [] }
  }
}`;

    const content = await aiComplete([
      { role: 'system', content: 'You are an expert LinkedIn profile optimizer. Return only valid JSON, no markdown.' },
      { role: 'user', content: prompt }
    ], { max_tokens: 3000, temperature: 0.7, response_format: { type: 'json_object' } });

    const parsed = JSON.parse(content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim());
    return { overallScore: parsed.overallScore, optimizations: parsed.optimizations };
  } catch (error) {
    console.error('Error optimizing LinkedIn:', error);
    throw error;
  }
};

export const explainCode = async (code) => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/ai/explain-code`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        messages: [
          { role: 'system', content: 'You are a code explanation expert. Provide detailed LINE-BY-LINE explanations using HTML formatting.' },
          { role: 'user', content: `Please provide a comprehensive explanation of this code:\n\n\`\`\`\n${code}\n\`\`\`` }
        ],
        temperature: 0.7,
        max_tokens: 3000
      })
    });
    if (!res.ok) throw new Error(`Explain code error: ${res.status}`);
    const data = await res.json();
    return data.choices[0].message.content;
  } catch (error) {
    console.error('Error explaining code:', error);
    throw error;
  }
};

export const generateResumeContent = async (resumeData) => {
  try {
    const { personalInfo, professionalSummary, workExperiences, education, skills, projects, certifications, targetRole } = resumeData;
    const prompt = `Create a professional resume for a ${targetRole} position based on the following information:

Personal Information:
- Name: ${personalInfo.fullName}
- Email: ${personalInfo.email}
- Phone: ${personalInfo.phone}
- Location: ${personalInfo.location}

Professional Summary:
${professionalSummary || 'Not provided'}

Work Experience:
${workExperiences.map(exp => `- Company: ${exp.company}\n- Position: ${exp.position}\n- Duration: ${exp.duration}\n- Description: ${exp.description}`).join('\n')}

Education:
${education.map(edu => `- Institution: ${edu.institution}\n- Degree: ${edu.degree}\n- Field: ${edu.field}\n- Year: ${edu.year}`).join('\n')}

Skills: ${skills || 'Not provided'}

Projects:
${projects.map(proj => `- Name: ${proj.name}\n- Technologies: ${proj.technologies}\n- Description: ${proj.description}`).join('\n')}

Certifications: ${certifications || 'Not provided'}

Enhance this resume content. Return in plain text with clear section headers.`;

    return await aiComplete([
      { role: 'system', content: 'You are a professional resume writer with expertise in creating ATS-friendly, compelling resumes.' },
      { role: 'user', content: prompt }
    ], { max_tokens: 2000, temperature: 0.7 });
  } catch (error) {
    console.error('Error generating resume content:', error);
    throw error;
  }
};

export const generateCareerGuidance = async (userData) => {
  try {
    const { skills, interests, education, experience, goals, industry } = userData;
    const prompt = `As a career advisor, provide comprehensive career guidance based on the following user profile:

Skills: ${skills.join(', ')}
Interests: ${interests.join(', ')}
Education Level: ${education}
Years of Experience: ${experience}
Career Goals: ${goals}
Preferred Industry: ${industry}

Provide a detailed career path analysis with sections: Career Path Analysis, Recommended Roles, Development Plan, Skill Gaps, Learning Resources, Networking Strategies, Industry Outlook.`;

    return await aiComplete([
      { role: 'system', content: 'You are an experienced career advisor with expertise in multiple industries.' },
      { role: 'user', content: prompt }
    ], { max_tokens: 2000, temperature: 0.7 });
  } catch (error) {
    console.error('Error generating career guidance:', error);
    throw error;
  }
};

export const analyzeATS = async (resumeText, jobDescription) => {
  try {
    let allKeywords = [];
    try {
      const content = await aiComplete([
        { role: 'system', content: 'You are a job requirement extraction expert. Return only valid JSON.' },
        { role: 'user', content: `Extract the 10-20 most important keywords from this job description. Return format: {"keywords": ["k1","k2"]}\n\n${jobDescription}` }
      ], { max_tokens: 400, temperature: 0, response_format: { type: 'json_object' } });
      const result = JSON.parse(content);
      allKeywords = (result.keywords || []).filter(k => k && k.trim().length > 1);
    } catch (e) {}

    const matchesKeyword = (keyword, text) => {
      const kLower = keyword.toLowerCase().trim();
      if (text.includes(kLower)) return true;
      const words = kLower.split(/\s+/);
      if (words.length >= 2) {
        const stopWords = new Set(['and','or','the','a','an','in','on','at','for','with','of','to','by']);
        const significant = words.filter(w => w.length > 3 && !stopWords.has(w));
        if (significant.length > 0) {
          const matched = significant.filter(w => text.includes(w));
          if (matched.length >= Math.max(1, Math.ceil(significant.length * 0.5))) return true;
        }
      }
      return false;
    };

    const resumeLower = resumeText.toLowerCase();
    const keywordMatches = allKeywords.filter(k => matchesKeyword(k, resumeLower));
    const missingKeywords = allKeywords.filter(k => !matchesKeyword(k, resumeLower));
    const keywordMatchPct = allKeywords.length > 0 ? Math.round((keywordMatches.length / allKeywords.length) * 100) : null;

    const analysisContent = await aiComplete([
      { role: 'system', content: 'You are an ATS compatibility expert. Return only valid JSON.' },
      { role: 'user', content: `Analyze this resume against the job description.\nKeywords: ${allKeywords.length} extracted, ${keywordMatches.length} matched, missing: ${missingKeywords.join(', ') || 'none'}\n\nJOB DESCRIPTION:\n${jobDescription}\n\nRESUME:\n${resumeText}\n\nReturn JSON: {"checklist":{"contactInfo":true,"education":true,"experience":true,"skills":true,"projects":true,"certifications":true},"formatScore":8,"formatRecommendations":[],"overallScore":85}` }
    ], { max_tokens: 600, temperature: 0, response_format: { type: 'json_object' } });

    const aiAnalysis = JSON.parse(analysisContent);
    const finalScore = keywordMatchPct !== null
      ? Math.min(100, Math.round((keywordMatchPct * 0.3) + (aiAnalysis.overallScore * 0.7)))
      : aiAnalysis.overallScore || 70;

    return {
      overallScore: finalScore,
      keywordMatches,
      missingKeywords,
      totalKeywords: allKeywords.length,
      checklist: aiAnalysis.checklist || { contactInfo: true, education: true, experience: true, skills: true, projects: true, certifications: true },
      formatScore: aiAnalysis.formatScore || 8,
      formatRecommendations: aiAnalysis.formatRecommendations || [],
      improvementSuggestions: missingKeywords.length > 0 ? missingKeywords.slice(0, 5).map(k => `Add keyword: '${k}'`) : ['Excellent keyword coverage!']
    };
  } catch (error) {
    console.error('Error analyzing ATS:', error);
    return { overallScore: 70, keywordMatches: [], missingKeywords: [], totalKeywords: 0, checklist: { contactInfo: true, education: true, experience: true, skills: true, projects: true, certifications: true }, formatScore: 8, formatRecommendations: [], improvementSuggestions: [] };
  }
};

export const transcribeAudio = async (audioBlob) => {
  try {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    const response = await fetch(`${API_BASE_URL}/api/interview-assistant/transcribe`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}` },
      body: formData
    });
    if (!response.ok) throw new Error(`Transcription error: ${response.status}`);
    const data = await response.json();
    return data.transcript;
  } catch (error) {
    console.error('Error transcribing audio:', error);
    throw error;
  }
};

export const generateCoverLetter = async (resumeText, jobDescription, keywords, companyName = '', hiringManagerName = '', jobTitle = '', personalInfo = {}) => {
  try {
    const greeting = hiringManagerName ? `Dear ${hiringManagerName},` : companyName ? `Dear ${companyName} Hiring Team,` : 'Dear Hiring Manager,';

    const prompt = `You are a professional cover letter writer. Generate a compelling, personalized cover letter IN FIRST PERSON.

JOB TITLE: ${jobTitle}
COMPANY NAME: ${companyName || 'the company'}
JOB DESCRIPTION:
${jobDescription}
CANDIDATE'S RESUME:
${resumeText}
KEYWORDS: ${keywords.join(', ')}

Start with ONLY the greeting: "${greeting}"
Write in FIRST PERSON (I, my, me). Reference actual companies, projects, technologies from the resume. 300-400 words. End with "Sincerely,"`;

    const coverLetterBody = await aiComplete([
      { role: 'system', content: 'You are an expert cover letter writer who creates personalized cover letters IN FIRST PERSON.' },
      { role: 'user', content: prompt }
    ], { max_tokens: 1200, temperature: 0.7 });

    let header = '';
    if (personalInfo.fullName) header += `${personalInfo.fullName}\n`;
    const contactInfo = [personalInfo.email, personalInfo.phone, personalInfo.location].filter(Boolean);
    if (contactInfo.length > 0) header += `${contactInfo.join(' | ')}\n`;
    if (personalInfo.linkedIn) header += `${personalInfo.linkedIn}\n`;
    if (header) header += '\n';

    return header + coverLetterBody.trim();
  } catch (error) {
    console.error('Error generating cover letter:', error);
    throw new Error('Failed to generate cover letter. Error: ' + error.message);
  }
};

export const generateInterviewQuestions = async (resumeText, jobDescription, jobTitle, companyName = '') => {
  try {
    const prompt = `Generate exactly 50 personalized interview questions with detailed answers.

JOB TITLE: ${jobTitle}
COMPANY: ${companyName || 'the company'}
JOB DESCRIPTION:
${jobDescription}
CANDIDATE'S RESUME:
${resumeText}

Generate EXACTLY 50 unique questions: 20 Technical, 15 Behavioral, 10 Role-specific, 5 General.
For each: clear question + first-person answer referencing resume details.

Return ONLY valid JSON: {"questions":[{"question":"...","answer":"..."}]}`;

    const content = await aiComplete([
      { role: 'system', content: 'You are an expert interview coach. Generate personalized interview questions. Always return valid JSON with a "questions" array of exactly 50 objects.' },
      { role: 'user', content: prompt }
    ], { max_tokens: 4096, temperature: 0.7 });

    const cleaned = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    const result = JSON.parse(cleaned);
    let questions = (result.questions || result.interviewQuestions || []).filter(q => q.question && q.answer);
    return questions.slice(0, 50);
  } catch (error) {
    console.error('Error generating interview questions:', error);
    throw new Error(`Failed to generate questions: ${error.message}`);
  }
};

export const generateResumeContentMock = async (resumeData) => {
  await new Promise(resolve => setTimeout(resolve, 2000));
  return `PROFESSIONAL SUMMARY\nExperienced ${resumeData.targetRole || 'Professional'} with a proven track record.\n\nSKILLS\n${resumeData.skills || 'Various technical skills'}`;
};

export default {
  getAIResponse,
  extractKeywords,
  tailorResume,
  explainCode,
  generateResumeContent,
  generateResumeContentMock,
  generateCareerGuidance,
  analyzeATS,
  transcribeAudio,
  generateCoverLetter,
  generateInterviewQuestions,
  optimizeLinkedIn
};
